import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { ThemeProvider } from "@material-ui/core/styles";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

import MonitoramentoPrecoFormSections from "./components/MonitoramentoPrecoFormSections";
import MonitoramentoPrecoHeader from "./components/MonitoramentoPrecoHeader";
import MonitoramentoPrecoLoading from "./components/MonitoramentoPrecoLoading";
import MonitoramentoPrecoMinhasColetasDialog from "./components/MonitoramentoPrecoMinhasColetasDialog";
import MonitoramentoPrecoUnavailable from "./components/MonitoramentoPrecoUnavailable";
import { STORAGE_KEY } from "./components/mpConstants";
import {
  buildConcorrentesRowsFromProduto,
  buildRazaoSocialOption,
  mapConcorrenteRowToApi,
  mapLojaFromApi,
  mapLojaToApiPayload,
  mapProdutoReferenciaFromApi,
  mapProdutoToApiPayload,
  normalizeLojaForm,
  unwrapApiResult,
} from "./components/mpMappers";
import { mpTheme, useMonitoramentoStyles } from "./components/mpStyles";
import {
  fileToBase64,
  getRepresentativeDisplayName,
  resolvePrecoKarstenNumerico,
} from "./components/mpUtils";

function useCatalogoInicial() {
  const [lojasCatalogo, setLojasCatalogo] = useState([]);
  const [produtosCatalogo, setProdutosCatalogo] = useState([]);
  const [pageState, setPageState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPageState("loading");
      try {
        const [lojasRes, prodRes] = await Promise.all([
          Api.get("lojas", "priceMonitoring", "", true),
          Api.get("produtos-referencia", "priceMonitoring", "", true),
        ]);
        if (cancelled) return;
        const lojasRaw = unwrapApiResult(lojasRes);
        const prodRaw = unwrapApiResult(prodRes);
        const lojas = Array.isArray(lojasRaw)
          ? lojasRaw.map(mapLojaFromApi)
          : [];
        const produtos = Array.isArray(prodRaw)
          ? prodRaw.map(mapProdutoReferenciaFromApi)
          : [];
        setLojasCatalogo(lojas);
        setProdutosCatalogo(produtos);
        if (lojas.length === 0 || produtos.length === 0) {
          setPageState("unavailable");
        } else {
          setPageState("ready");
        }
      } catch {
        if (!cancelled) {
          setLojasCatalogo([]);
          setProdutosCatalogo([]);
          setPageState("unavailable");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { lojasCatalogo, produtosCatalogo, pageState };
}

export default function MonitoramentoPreco() {
  const classes = useMonitoramentoStyles();
  const snackbar = useSnackbar();
  const snackbarRef = useRef(snackbar);
  snackbarRef.current = snackbar;

  const { lojasCatalogo, produtosCatalogo, pageState } = useCatalogoInicial();

  const [tipoColeta, setTipoColeta] = useState("padrao");
  const [dataColeta, setDataColeta] = useState(() =>
    moment().format("DD-MM-YYYY"),
  );
  const [representante] = useState(() => getRepresentativeDisplayName());

  const [razaoSocial, setRazaoSocial] = useState(null);
  const [lojaCompleta, setLojaCompleta] = useState(null);

  const [produto, setProduto] = useState(null);
  const [precoKarstenLoja, setPrecoKarstenLoja] = useState(undefined);
  const [concorrentesRows, setConcorrentesRows] = useState([]);

  const [coletas, setColetas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [minhasColetasOpen, setMinhasColetasOpen] = useState(false);
  const [minhasColetasMountKey, setMinhasColetasMountKey] = useState(0);
  const storageWarnedRef = useRef(false);

  const razoesOptions = useMemo(() => {
    const map = new Map();
    lojasCatalogo.forEach((l) => {
      const key = l.razaoSocial;
      if (!map.has(key)) {
        map.set(key, buildRazaoSocialOption(key, l.cnpj));
      }
    });
    return Array.from(map.values());
  }, [lojasCatalogo]);

  const lojaGrupoOptions = useMemo(
    () => (lojaCompleta ? [lojaCompleta] : []),
    [lojaCompleta],
  );

  const handleRazaoSocialChange = useCallback(
    (v) => {
      setRazaoSocial(v);
      if (!v?.razaoSocial) {
        setLojaCompleta(null);
        return;
      }
      const matches = lojasCatalogo.filter(
        (l) => l.razaoSocial === v.razaoSocial,
      );
      if (matches.length === 0) {
        setLojaCompleta(null);
        return;
      }
      const sorted = [...matches].sort((a, b) => {
        const cmp = (a.grupoEconomico || "").localeCompare(
          b.grupoEconomico || "",
          "pt-BR",
        );
        if (cmp !== 0) return cmp;
        return String(a.id).localeCompare(String(b.id), "pt-BR");
      });
      setLojaCompleta(sorted[0]);
    },
    [lojasCatalogo],
  );

  const handleLojaCompletaChange = useCallback(
    (loja) => {
      setLojaCompleta(normalizeLojaForm(loja));
      if (!loja?.razaoSocial) return;
      const sample =
        lojasCatalogo.find((l) => l.id === loja.id) ??
        lojasCatalogo.find(
          (l) =>
            l.razaoSocial === loja.razaoSocial &&
            l.idGrupoEconomico === loja.idGrupoEconomico,
        );
      setRazaoSocial(
        buildRazaoSocialOption(loja.razaoSocial, sample?.cnpj ?? loja.cnpj),
      );
    },
    [lojasCatalogo],
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setColetas(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(coletas));
    } catch {
      if (!storageWarnedRef.current) {
        storageWarnedRef.current = true;
        snackbarRef.current.showMessage(
          "Não foi possível salvar o rascunho no armazenamento local (limite ou dados muito grandes).",
          { severity: "warning" },
        );
      }
    }
  }, [coletas]);

  const handleProdutoChange = useCallback(
    (v) => {
      setProduto(v);
      if (!v) {
        setConcorrentesRows([]);
        setPrecoKarstenLoja("");
        return;
      }
      const full = produtosCatalogo.find((c) => c.id === v.id) || v;
      setPrecoKarstenLoja("");
      setConcorrentesRows(buildConcorrentesRowsFromProduto(full));
    },
    [produtosCatalogo],
  );

  const updateConcorrente = useCallback((index, patch) => {
    setConcorrentesRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.encontrado === false) {
          return {
            ...next,
            precoNormal: undefined,
            emPromocao: false,
            precoPromocional: undefined,
            fotoBase64: "",
          };
        }
        return next;
      }),
    );
  }, []);

  const isCurrentFormValid = useMemo(() => {
    if (!lojaCompleta || !produto) return false;
    const precoN = resolvePrecoKarstenNumerico(precoKarstenLoja);
    if (precoN == null || precoN <= 0) return false;
    for (const row of concorrentesRows) {
      if (!row.encontrado) continue;
      if (row.precoNormal == null || row.precoNormal <= 0) return false;
      if (!row.fotoBase64) return false;
      if (row.emPromocao) {
        if (row.precoPromocional == null || row.precoPromocional <= 0) {
          return false;
        }
      }
    }
    return true;
  }, [lojaCompleta, produto, precoKarstenLoja, concorrentesRows]);

  const buildPayloadItem = useCallback(() => {
    const precoSalvo = resolvePrecoKarstenNumerico(precoKarstenLoja);
    return {
      id: editingId ?? `coleta_${Date.now()}`,
      tipoColeta,
      dataColeta,
      representante,
      loja: mapLojaToApiPayload(lojaCompleta),
      produto: mapProdutoToApiPayload(produto),
      precoKarstenLoja: precoSalvo,
      concorrentes: concorrentesRows.map((r) => ({ ...r })),
    };
  }, [
    editingId,
    tipoColeta,
    dataColeta,
    representante,
    lojaCompleta,
    produto,
    precoKarstenLoja,
    concorrentesRows,
  ]);

  const handleAdicionarColeta = () => {
    if (!isCurrentFormValid) {
      snackbar.showMessage(
        "Preencha os campos obrigatórios: loja, SKU Karsten, preço Karsten e, para cada concorrente com produto encontrado, preço normal, foto e preço promocional quando em promoção.",
        { severity: "warning" },
      );
      return;
    }
    const item = buildPayloadItem();
    setColetas((prev) => {
      const idx = prev.findIndex((c) => c.id === item.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = item;
        return copy;
      }
      return [...prev, item];
    });
    setEditingId(null);
    setProduto(null);
    setPrecoKarstenLoja("");
    setConcorrentesRows([]);
    snackbar.showMessage("Produto adicionado à coleta da sessão.", {
      severity: "success",
    });
  };

  const handleEditColeta = (row) => {
    setEditingId(row.id);
    setTipoColeta(row.tipoColeta);
    setDataColeta(row.dataColeta);
    handleLojaCompletaChange(row.loja);
    const idRef = row.produto?.id;
    let refProduto = produtosCatalogo.find((p) => p.id === idRef);
    if (!refProduto && row.produto) {
      refProduto = {
        ...row.produto,
        listaSkuNome: `${row.produto.sku} - ${row.produto.nome}`,
        linha: row.produto.linha ?? "",
        categoria: row.produto.categoria ?? "",
        gramatura: row.produto.gramatura ?? "",
        tecidoBase: row.produto.tecidoBase ?? "",
        precoKarsten: row.precoKarstenLoja,
        imagemUrl: row.produto.imagemUrl ?? "",
        concorrentesNomes: (row.concorrentes ?? []).map((x) => x.nome),
        concorrentesPlanejados: [],
      };
    } else if (refProduto && row.produto) {
      refProduto = {
        ...refProduto,
        gramatura: row.produto.gramatura ?? refProduto.gramatura,
        tecidoBase: row.produto.tecidoBase ?? refProduto.tecidoBase,
      };
    }
    setProduto(refProduto || null);
    const precoRow = resolvePrecoKarstenNumerico(row.precoKarstenLoja);
    setPrecoKarstenLoja(precoRow != null ? precoRow : "");
    const catalogHint = produtosCatalogo.find((p) => p.id === idRef);
    const refPorNome = {};
    (catalogHint?.concorrentesPlanejados ?? []).forEach((p) => {
      if (p.nome) refPorNome[p.nome] = p.produtoReferencia ?? "";
    });
    setConcorrentesRows(
      (row.concorrentes || []).map((r) => {
        const inferredFound =
          r.precoNormal != null &&
          r.precoNormal !== "" &&
          !Number.isNaN(Number(r.precoNormal)) &&
          Number(r.precoNormal) > 0;
        const encontrado =
          typeof r.encontrado === "boolean" ? r.encontrado : inferredFound;
        return {
          nome: r.nome,
          produtoReferencia: r.produtoReferencia ?? refPorNome[r.nome] ?? "",
          encontrado,
          precoNormal: r.precoNormal,
          emPromocao: r.emPromocao,
          precoPromocional: r.precoPromocional,
          fotoBase64: r.fotoBase64 || "",
        };
      }),
    );
  };

  const handleRemoveColeta = (id) => {
    setColetas((prev) => prev.filter((c) => c.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setProduto(null);
      setPrecoKarstenLoja("");
      setConcorrentesRows([]);
    }
  };

  const handleFinalizar = async () => {
    if (!coletas.length) {
      snackbar.showMessage("Adicione ao menos um produto à coleta.", {
        severity: "warning",
      });
      return;
    }
    setFinalizando(true);
    try {
      const coletasPayload = coletas.map((item) => ({
        id: item.id,
        tipoColeta: item.tipoColeta,
        dataColeta: item.dataColeta,
        representante: item.representante,
        loja: mapLojaToApiPayload(item.loja),
        produto: mapProdutoToApiPayload(item.produto),
        precoKarstenLoja: item.precoKarstenLoja,
        concorrentes: (item.concorrentes || []).map(mapConcorrenteRowToApi),
      }));
      const res = await Api.post(
        "coletas",
        "priceMonitoring",
        { coletas: coletasPayload },
        true,
      );
      const result = unwrapApiResult(res);
      const msg =
        result?.mensagem ?? result?.Mensagem ?? "Coleta enviada com sucesso.";
      localStorage.removeItem(STORAGE_KEY);
      setColetas([]);
      setEditingId(null);
      snackbar.showMessage(msg, { severity: "success" });
    } catch (e) {
      snackbar.showMessage(
        e?.message ? `Erro ao enviar: ${e.message}` : "Erro ao enviar coleta.",
        { severity: "error" },
      );
    } finally {
      setFinalizando(false);
    }
  };

  const handleFotoChange = async (index, file) => {
    if (!file) {
      updateConcorrente(index, { fotoBase64: "" });
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      updateConcorrente(index, { fotoBase64: b64 });
    } catch {
      snackbar.showMessage("Não foi possível ler a imagem.", {
        severity: "error",
      });
    }
  };

  const formProps = {
    tipoColeta,
    setTipoColeta,
    dataColeta,
    setDataColeta,
    representante,
    razaoSocial,
    handleRazaoSocialChange,
    lojaCompleta,
    setLojaCompleta: handleLojaCompletaChange,
    razoesOptions,
    lojaGrupoOptions,
    produto,
    handleProdutoChange,
    produtosCatalogo,
    precoKarstenLoja,
    setPrecoKarstenLoja,
    concorrentesRows,
    updateConcorrente,
    handleFotoChange,
    coletas,
    handleEditColeta,
    handleRemoveColeta,
    handleAdicionarColeta,
    handleFinalizar,
    isCurrentFormValid,
    finalizando,
  };

  return (
    <ThemeProvider theme={mpTheme}>
      <div className={classes.pageRoot}>
        <div className={classes.inner}>
          {pageState === "loading" && (
            <MonitoramentoPrecoLoading classes={classes} />
          )}
          {pageState === "unavailable" && (
            <MonitoramentoPrecoUnavailable classes={classes} />
          )}
          {pageState === "ready" && (
            <>
              <MonitoramentoPrecoHeader
                classes={classes}
                onMinhasColetas={() => {
                  setMinhasColetasMountKey((k) => k + 1);
                  setMinhasColetasOpen(true);
                }}
              />
              <MonitoramentoPrecoMinhasColetasDialog
                key={minhasColetasMountKey}
                open={minhasColetasOpen}
                onClose={() => setMinhasColetasOpen(false)}
                classes={classes}
              />
              <MonitoramentoPrecoFormSections
                classes={classes}
                form={formProps}
              />
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
